import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { loadUserSuccess } from '@/app/dashboard/store/dashboard.actions';
import { LanguageService } from '@core/i18n/language.service';
import { PaginationResponse } from '@shared/models/common.model';
import { NotificationService } from '@shared/services/notification.service';
import {
  findNotificationItemByKey,
  mapInAppNotificationToItem,
  resolveMarkReadNotificationId,
} from '@shared/utils/build-notification';

import * as NotificationsActions from './notifications.actions';
import { NOTIFICATIONS_PAGE_SIZE } from './notifications.actions';
import {
  selectNotifications,
  selectNotificationsHydrated,
} from './notifications.selectors';

function emptyPagination(page: number, limit: number): PaginationResponse {
  return { page, limit, total: 0, totalPages: 0 };
}

@Injectable()
export class NotificationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly notificationService = inject(NotificationService);
  private readonly language = inject(LanguageService);

  hydrateOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSuccess),
      withLatestFrom(this.store.select(selectNotificationsHydrated)),
      filter(([, hydrated]) => !hydrated),
      map(() =>
        NotificationsActions.loadNotifications({
          page: 1,
          limit: NOTIFICATIONS_PAGE_SIZE,
          append: false,
        })
      )
    )
  );

  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.loadNotifications),
      exhaustMap(
        ({ page = 1, limit = NOTIFICATIONS_PAGE_SIZE, append = false }) => {
          const language = this.language.current();
          const list$ = this.notificationService.listNotifications({
            page,
            limit,
            orderBy: 'occurredAt',
            order: 'DESC',
            status: 'all',
            language,
          });

          if (append) {
            return list$.pipe(
              map(list =>
                NotificationsActions.loadNotificationsSuccess({
                  items: (list?.data ?? []).map(mapInAppNotificationToItem),
                  pagination: list?.pagination ?? emptyPagination(page, limit),
                  append: true,
                })
              ),
              catchError(error =>
                of(NotificationsActions.loadNotificationsFailure({ error }))
              )
            );
          }

          return forkJoin({
            list: list$,
            unread: this.notificationService.getUnreadCount(),
          }).pipe(
            map(({ list, unread }) =>
              NotificationsActions.loadNotificationsSuccess({
                items: (list?.data ?? []).map(mapInAppNotificationToItem),
                pagination: list?.pagination ?? emptyPagination(page, limit),
                append: false,
                unreadCount: unread?.data?.count ?? 0,
              })
            ),
            catchError(error =>
              of(NotificationsActions.loadNotificationsFailure({ error }))
            )
          );
        }
      )
    )
  );

  markNotificationRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsActions.markNotificationRead),
        withLatestFrom(this.store.select(selectNotifications)),
        mergeMap(([{ id }, items]) => {
          const item = findNotificationItemByKey(items, id);
          if (!item) {
            return EMPTY;
          }

          const notificationId = resolveMarkReadNotificationId(item);
          if (!notificationId) {
            this.store.dispatch(
              NotificationsActions.markNotificationReadFailure({
                id: item.clientKey,
                error: new Error('Notification has no server notificationId'),
              })
            );
            return EMPTY;
          }

          return this.notificationService
            .markRead({ notificationIds: [notificationId] })
            .pipe(
              catchError(error => {
                this.store.dispatch(
                  NotificationsActions.markNotificationReadFailure({
                    id: item.clientKey,
                    error,
                  })
                );
                return EMPTY;
              }),
              switchMap(() => EMPTY)
            );
        })
      ),
    { dispatch: false }
  );

  markAllNotificationsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsActions.markAllNotificationsRead),
        switchMap(() =>
          this.notificationService.markAllRead().pipe(
            catchError(error => {
              this.store.dispatch(
                NotificationsActions.markAllNotificationsReadFailure({
                  error,
                })
              );
              return EMPTY;
            }),
            switchMap(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );
}
